import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authApi } from './authApi';
import { useAuth } from '@/contexts/AuthContext';
import type { LoginRequest, ProfileUpdate, ChangePasswordRequest } from '@/types';

export function useLogin() {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      queryClient.clear();
      setUser(response.user);
      queryClient.setQueryData(['auth', 'me'], response.user);
    },
  });
}

export function useProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.getProfile(),
    retry: false,
    ...options,
  });
}

export function useMemberships() {
  return useQuery({
    queryKey: ['auth', 'memberships'],
    queryFn: () => authApi.memberships(),
    retry: false,
  });
}

export function useSwitchContext() {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  return useMutation({
    mutationFn: (tenantId: string) => authApi.switchContext(tenantId),
    onSuccess: async (user) => {
      await queryClient.cancelQueries();
      queryClient.clear();
      setUser(user);
      queryClient.setQueryData(['auth', 'me'], user);
      window.location.assign('/app/dashboard');
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  return useMutation({
    mutationFn: (data: ProfileUpdate) => authApi.updateProfile(data),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(['auth', 'me'], user);
      toast.success('Perfil actualizado');
    },
    onError: (error: unknown) => {
      const apiDetail = (error as { response?: { data?: { detail?: string } } } | null | undefined)?.response?.data?.detail;
      toast.error(apiDetail || 'No se pudo actualizar el perfil');
    },
  });
}

/**
 * Cambio de contraseña de la propia identidad.
 *
 * Un solo hook para los dos casos que tenía duplicados, porque la única
 * diferencia real es de política de navegación y de dónde se pinta el error:
 *
 * - El servidor revoca **todas** las sesiones al cambiarla, incluida la actual,
 *   y limpia las cookies. Con `redirectToLogin` la caché se vacía y se vuelve al
 *   login en lugar de intentar refrescar el perfil: la sesión ya no existe.
 * - Sin `redirectToLogin` solo se invalida `auth`. Sirve dentro del shell, donde
 *   perder la sesión a mitad de trabajo sería una sorpresa, y la siguiente
 *   navegación falla con 401 y el interceptor lleva al login por su cuenta.
 * - Con `errorToast: false` el error no se duplica: la pantalla que ya lo pinta
 *   en un `AlertBanner` junto al campo no necesita además un toast con el mismo
 *   texto, que obliga a leerlo dos veces y desaparece solo.
 */
export interface UseChangePasswordOptions {
  /** Vaciar caché y volver al login: la sesión ya no existe tras el cambio. */
  redirectToLogin?: boolean;
  /** Emitir `toast.error`. Desactívalo si la pantalla ya muestra el error. */
  errorToast?: boolean;
  /** Confirmación de éxito. Por defecto, un toast. */
  successToast?: boolean;
}

export function useChangePassword(options: UseChangePasswordOptions = {}) {
  const { redirectToLogin = false, errorToast = false, successToast = true } = options;
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authApi.changePassword(data),
    onSuccess: async () => {
      if (successToast) toast.success('Contraseña actualizada');
      if (redirectToLogin) {
        queryClient.clear();
        setUser(null);
        window.location.replace('/login');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: unknown) => {
      if (!errorToast) return;
      const apiDetail = (error as { response?: { data?: { detail?: string } } } | null | undefined)?.response?.data?.detail;
      toast.error(apiDetail || 'No se pudo actualizar la contraseña');
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      queryClient.clear();
      setUser(null);
      window.location.href = '/login';
    },
  });
}
